import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaEdit, 
  FaToggleOn, 
  FaToggleOff,
  FaSearch,
  FaBell,
  FaBellSlash,
  FaClock,
  FaEnvelope,
  FaMobile,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaCheck,
  FaExclamationTriangle,
  FaCog,
  FaUndo,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import TemplateEditor from './TemplateEditor';
import { CATEGORY_METADATA, notificationApi } from '../../api/notifications';
import { notificationSettingsApi } from '../../api/notificationSettings';
import { generateOOTBTemplates } from '../../data/templateData';
import styles from './TemplateManagement.module.css';

// Define which notification types are "Critical" (important ones users shouldn't miss)
const CRITICAL_NOTIFICATION_TYPES = [
  // Payment & Billing
  'PAYMENT_RECEIVED', 'PAYMENT_FAILED', 'PAYMENT_REMINDER', 'REFUND_PROCESSED',
  // Schedule Changes
  'EVENT_CANCELLED', 'EVENT_RESCHEDULED', 'SCHEDULE_CHANGE',
  // Important Updates
  'REGISTRATION_APPROVED', 'REGISTRATION_DECLINED', 'REGISTRATION_WAITLIST',
  'PROGRAM_CANCELLED', 'TEAM_ASSIGNMENT',
  // Urgent
  'EMERGENCY_ALERT', 'WEATHER_CANCELLATION', 'FACILITY_CLOSURE'
];

/**
 * TemplateManagement Component - Redesigned
 * 
 * Features:
 * - Smart presets: All / Critical Only / Custom
 * - Two-panel layout for custom mode
 * - Category-based organization
 * - Simplified per-notification toggles
 */
const TemplateManagement = ({ clubId }) => {
  // ============ STATE ============
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  
  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // Club Details
  const [clubDetails, setClubDetails] = useState(null);
  
  // New UI State
  const [presetMode, setPresetMode] = useState('all'); // 'all' | 'critical' | 'custom'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [channelDefaults, setChannelDefaults] = useState({ email: true, inApp: true });

  const { addNotification } = useNotification();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // ============ DATA LOADING ============
  
  const loadTemplates = async () => {
    try {
      let clubTemplates;
      
      try {
        const response = await notificationApi.getTemplates(clubId);
        let templatesData = response.templates || response.data?.templates || [];
        
        if (typeof templatesData === 'object' && !Array.isArray(templatesData)) {
          clubTemplates = [];
          Object.entries(templatesData).forEach(([categoryKey, categoryTemplates]) => {
            if (Array.isArray(categoryTemplates)) {
              clubTemplates.push(...categoryTemplates);
            }
          });
        } else if (Array.isArray(templatesData)) {
          clubTemplates = templatesData;
        } else {
          clubTemplates = [];
        }
      } catch (apiError) {
        clubTemplates = generateOOTBTemplates(clubId);
      }
      
      const availableTemplates = clubTemplates.filter(template => 
        !template.isSystemOnly && template.category !== 'system'
      ).map(template => ({
        ...template,
        isActive: template.isActive !== undefined ? template.isActive : true
      }));
      
      setTemplates(availableTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      addNotification({ message: 'Failed to load templates', type: 'error' });
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await notificationSettingsApi.getSettings(clubId);
      if (response?.success) {
        setNotificationSettings(response.settings);
        
        // Detect current preset mode based on settings
        detectPresetMode(response.settings);
      } else {
        const defaultSettings = {
          globalSettings: { allNotificationsEnabled: true },
          categories: {},
          quietHours: { enabled: false, startTime: '22:00', endTime: '08:00' }
        };
        setNotificationSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
      const defaultSettings = {
        globalSettings: { allNotificationsEnabled: true },
        categories: {},
        quietHours: { enabled: false, startTime: '22:00', endTime: '08:00' }
      };
      setNotificationSettings(defaultSettings);
    }
  };

  const loadClubDetails = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/clubs/${clubId}`);
      if (response.ok) {
        const data = await response.json();
        setClubDetails(data);
      } else {
        setClubDetails({ name: 'Club' });
      }
    } catch (error) {
      console.error('Error loading club details:', error);
      setClubDetails({ name: 'Club' });
    }
  };

  // Detect which preset mode the current settings match
  const detectPresetMode = (settings) => {
    if (!settings?.categories) {
      setPresetMode('all');
      return;
    }
    
    // Check if all are enabled
    let allEnabled = true;
    let onlyCriticalEnabled = true;
    
    Object.values(settings.categories).forEach(category => {
      category.notifications?.forEach(notif => {
        if (!notif.enabled) allEnabled = false;
        if (CRITICAL_NOTIFICATION_TYPES.includes(notif.type) && !notif.enabled) {
          onlyCriticalEnabled = false;
        }
        if (!CRITICAL_NOTIFICATION_TYPES.includes(notif.type) && notif.enabled) {
          onlyCriticalEnabled = false;
        }
      });
    });
    
    if (allEnabled) {
      setPresetMode('all');
    } else if (onlyCriticalEnabled) {
      setPresetMode('critical');
    } else {
      setPresetMode('custom');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setSettingsLoading(true);
      
      await Promise.allSettled([
        loadTemplates(),
        loadNotificationSettings(),
        loadClubDetails()
      ]);
      
      setLoading(false);
      setSettingsLoading(false);
    };

    if (clubId) {
      loadData();
    }
  }, [clubId]);

  // ============ HELPER FUNCTIONS ============
  // (Defined before useMemo hooks that use them)
  
  const getNotificationSettings = (template) => {
    if (!notificationSettings?.categories) return { enabled: true, channels: { inApp: true, email: true } };
    const category = template.category;
    const notification = notificationSettings.categories[category]?.notifications?.find(n => n.type === template.type);
    return notification || { enabled: true, channels: { inApp: true, email: true } };
  };

  const getNotificationEnabled = (template) => {
    const settings = getNotificationSettings(template);
    return settings?.enabled && notificationSettings?.globalSettings?.allNotificationsEnabled;
  };

  // ============ COMPUTED VALUES ============
  
  // Group templates by category
  const templatesByCategory = useMemo(() => {
    const grouped = {};
    templates.forEach(template => {
      const category = template.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(template);
    });
    return grouped;
  }, [templates]);

  // Get categories with counts
  const categories = useMemo(() => {
    return Object.entries(templatesByCategory).map(([key, temps]) => {
      const meta = CATEGORY_METADATA[key] || { name: key, icon: '📋' };
      const enabledCount = temps.filter(t => getNotificationEnabled(t)).length;
      return {
        key,
        name: meta.name,
        icon: meta.icon,
        total: temps.length,
        enabled: enabledCount
      };
    }).sort((a, b) => b.total - a.total);
  }, [templatesByCategory, notificationSettings]);

  // Get templates for selected category (with search filter)
  const displayedTemplates = useMemo(() => {
    let temps = selectedCategory 
      ? (templatesByCategory[selectedCategory] || [])
      : templates;
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      temps = temps.filter(t => 
        t.name.toLowerCase().includes(term) ||
        t.type.toLowerCase().includes(term)
      );
    }
    
    return temps.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCategory, templatesByCategory, templates, searchTerm]);

  // Stats
  const stats = useMemo(() => {
    const total = templates.length;
    const enabled = templates.filter(t => getNotificationEnabled(t)).length;
    const customized = templates.filter(t => t.isCustomized).length;
    return { total, enabled, customized };
  }, [templates, notificationSettings]);

  // ============ HANDLERS ============
  
  // Apply preset mode (sequential awaits to prevent race conditions)
  const applyPreset = async (mode) => {
    setPresetMode(mode);
    
    if (mode === 'all') {
      await handleMasterToggle(true);
      // Process sequentially to prevent race conditions on state updates
      for (const template of templates) {
        try {
          await handleNotificationToggle(template.type, true, true);
        } catch {
          // Continue with remaining templates even if one fails
        }
      }
      addNotification({ message: 'All notifications enabled', type: 'success' });
    } else if (mode === 'critical') {
      await handleMasterToggle(true);
      for (const template of templates) {
        try {
          const isCritical = CRITICAL_NOTIFICATION_TYPES.includes(template.type);
          await handleNotificationToggle(template.type, isCritical, true);
        } catch {
          // Continue with remaining templates
        }
      }
      addNotification({ message: 'Only critical notifications enabled', type: 'success' });
    }
    // 'custom' mode doesn't change anything, just shows the customization UI
  };

  const handleMasterToggle = async (enabled) => {
    try {
      const response = await notificationSettingsApi.updateGlobalSettings(clubId, {
        allNotificationsEnabled: enabled
      });
      
      if (response?.success) {
        setNotificationSettings(prev => ({
          ...prev,
          globalSettings: { ...prev.globalSettings, allNotificationsEnabled: enabled }
        }));
        if (!enabled) {
          addNotification({ message: 'All notifications paused', type: 'info' });
        }
      }
    } catch (error) {
      console.error('Error updating master toggle:', error);
      addNotification({ message: 'Failed to update notification settings', type: 'error' });
    }
  };

  const handleNotificationToggle = async (templateType, enabled, silent = false) => {
    try {
      const response = await notificationSettingsApi.updateNotificationTypeSettings(clubId, templateType, {
        enabled,
        channels: { inApp: channelDefaults.inApp, email: channelDefaults.email }
      });
      
      if (response?.success) {
        setNotificationSettings(prev => {
          const updated = { ...prev };
          if (!updated.categories) updated.categories = {};
          
          const template = templates.find(t => t.type === templateType);
          const category = template?.category || 'club';
          
          if (!updated.categories[category]) {
            updated.categories[category] = { notifications: [] };
          }
          
          const existingIndex = updated.categories[category].notifications.findIndex(n => n.type === templateType);
          if (existingIndex >= 0) {
            updated.categories[category].notifications[existingIndex].enabled = enabled;
          } else {
            updated.categories[category].notifications.push({
              type: templateType,
              enabled,
              channels: { inApp: true, email: true }
            });
          }
          
          return updated;
        });
        
        if (!silent) {
          addNotification({ 
            message: `${templateType.replace(/_/g, ' ')} ${enabled ? 'enabled' : 'disabled'}`, 
            type: 'success' 
          });
        }
      }
    } catch (error) {
      console.error('Error updating notification toggle:', error);
      if (!silent) {
        addNotification({ message: 'Failed to update notification setting', type: 'error' });
      }
    }
  };

  const handleCategoryBulkToggle = async (categoryKey, enabled) => {
    const categoryTemplates = templatesByCategory[categoryKey] || [];
    
    for (const template of categoryTemplates) {
      await handleNotificationToggle(template.type, enabled, true);
    }
    
    addNotification({
      message: `${enabled ? 'Enabled' : 'Disabled'} all ${CATEGORY_METADATA[categoryKey]?.name || categoryKey} notifications`,
      type: 'success'
    });
  };

  const handleQuietHoursUpdate = async (quietHours) => {
    try {
      const response = await notificationSettingsApi.updateQuietHours(clubId, quietHours);
      
      if (response?.success) {
        setNotificationSettings(prev => ({
          ...prev,
          quietHours
        }));
        addNotification({ message: 'Quiet hours updated', type: 'success' });
      }
    } catch (error) {
      console.error('Error updating quiet hours:', error);
      addNotification({ message: 'Failed to update quiet hours', type: 'error' });
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleSaveTemplate = async (updatedTemplate) => {
    try {
      const savePayload = {
        ...updatedTemplate,
        clubId: clubId,
        isSystemDefault: false,
        hasCustomizations: true,
        lastModified: new Date().toISOString()
      };
      
      const response = await notificationApi.updateTemplate(clubId, editingTemplate.type, savePayload);
      
      if (response?.success && response?.data) {
        const savedTemplate = response.data;
        
        setTemplates(prev => prev.map(template => 
          template.type === editingTemplate.type
            ? { 
                ...template, 
                ...savedTemplate,
                isCustomized: true,
                isSystemDefault: false,
                hasCustomizations: true,
                isActive: true,
                lastModified: new Date(savedTemplate.lastModified || new Date()),
                customizedBy: clubDetails?.name || 'This Club'
              }
            : template
        ));
        
        addNotification({ 
          message: `Template customized for ${clubDetails?.name || 'your club'}`, 
          type: 'success' 
        });
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      
      setTemplates(prev => prev.map(template => 
        template._id === updatedTemplate._id 
          ? { 
              ...template, 
              ...updatedTemplate, 
              clubId: clubId,
              isSystemDefault: false,
              isCustomized: true,
              hasCustomizations: true,
              isActive: true,
              lastModified: new Date(),
              customizedBy: clubDetails?.name || 'This Club'
            }
          : template
      ));
      
      addNotification({ 
        message: 'Template saved locally (API unavailable)', 
        type: 'warning' 
      });
    } finally {
      setShowEditor(false);
      setEditingTemplate(null);
    }
  };

  const handleCancelEdit = () => {
    setShowEditor(false);
    setEditingTemplate(null);
  };

  const toggleCategoryExpand = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  // ============ RENDER ============
  
  if (showEditor) {
    return (
      <div className={styles.editorContainer}>
        <TemplateEditor
          clubId={clubId}
          templateType={editingTemplate?.type}
          initialTemplate={editingTemplate}
          onSave={handleSaveTemplate}
          onCancel={handleCancelEdit}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>⏳</div>
        <p>Loading notification settings...</p>
      </div>
    );
  }

  return (
    <div className={styles.notificationManager}>
      {/* Compact Header with Master Toggle */}
      <div className={styles.compactHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FaBell className={styles.titleIcon} />
            Notification Settings
          </h1>
          <span className={styles.statsInline}>{stats.enabled}/{stats.total} active</span>
        </div>
        
        <div className={styles.headerRight}>
          <button
            onClick={() => handleMasterToggle(!notificationSettings?.globalSettings?.allNotificationsEnabled)}
            className={`${styles.masterBtn} ${notificationSettings?.globalSettings?.allNotificationsEnabled ? styles.masterOn : styles.masterOff}`}
            disabled={settingsLoading}
          >
            {notificationSettings?.globalSettings?.allNotificationsEnabled ? (
              <>
                <FaBell /> Notifications ON
              </>
            ) : (
              <>
                <FaBellSlash /> Paused
              </>
            )}
          </button>
        </div>
      </div>

      {/* Compact Controls Row */}
      <div className={styles.controlsRow}>
        {/* Preset Pills */}
        <div className={styles.presetPills}>
          <span className={styles.controlLabel}>Level:</span>
          <button 
            className={`${styles.presetPill} ${presetMode === 'all' ? styles.pillActive : ''}`}
            onClick={() => applyPreset('all')}
          >
            🔔 All
          </button>
          <button 
            className={`${styles.presetPill} ${presetMode === 'critical' ? styles.pillActive : ''}`}
            onClick={() => applyPreset('critical')}
          >
            ⚡ Critical
          </button>
          <button 
            className={`${styles.presetPill} ${presetMode === 'custom' ? styles.pillActive : ''}`}
            onClick={() => setPresetMode('custom')}
          >
            ⚙️ Custom
          </button>
        </div>

        {/* Channel Toggles */}
        <div className={styles.channelPills}>
          <span className={styles.controlLabel}>Channels:</span>
          <button
            className={`${styles.channelPill} ${channelDefaults.email ? styles.channelActive : ''}`}
            onClick={() => setChannelDefaults(prev => ({ ...prev, email: !prev.email }))}
          >
            <FaEnvelope /> Email
          </button>
          <button
            className={`${styles.channelPill} ${channelDefaults.inApp ? styles.channelActive : ''}`}
            onClick={() => setChannelDefaults(prev => ({ ...prev, inApp: !prev.inApp }))}
          >
            <FaMobile /> In-App
          </button>
        </div>

        {/* Quiet Hours Toggle */}
        <div className={styles.quietPill}>
          <button
            className={`${styles.quietBtn} ${notificationSettings?.quietHours?.enabled ? styles.quietActive : ''}`}
            onClick={() => handleQuietHoursUpdate({
              ...notificationSettings?.quietHours,
              enabled: !notificationSettings?.quietHours?.enabled
            })}
          >
            <FaClock />
            {notificationSettings?.quietHours?.enabled 
              ? `Quiet ${notificationSettings?.quietHours?.startTime || '22:00'}-${notificationSettings?.quietHours?.endTime || '08:00'}`
              : 'Quiet Hours'
            }
          </button>
          {notificationSettings?.quietHours?.enabled && (
            <div className={styles.quietTimeInputs}>
              <input
                type="time"
                value={notificationSettings?.quietHours?.startTime || '22:00'}
                onChange={(e) => handleQuietHoursUpdate({
                  ...notificationSettings?.quietHours,
                  startTime: e.target.value
                })}
              />
              <span>-</span>
              <input
                type="time"
                value={notificationSettings?.quietHours?.endTime || '08:00'}
                onChange={(e) => handleQuietHoursUpdate({
                  ...notificationSettings?.quietHours,
                  endTime: e.target.value
                })}
              />
            </div>
          )}
        </div>
      </div>

      {/* Custom Configuration (Two-Panel Layout) */}
      {presetMode === 'custom' && (
        <div className={styles.customSection}>
          <h2 className={styles.sectionTitle}>Custom Configuration</h2>
          
          <div className={styles.twoPanel}>
            {/* Left Panel - Categories */}
            <div className={styles.categoryPanel}>
              <div className={styles.categoryList}>
                <button
                  className={`${styles.categoryItem} ${!selectedCategory ? styles.categoryActive : ''}`}
                  onClick={() => setSelectedCategory(null)}
                >
                  <span className={styles.categoryIcon}>📋</span>
                  <span className={styles.categoryName}>All Categories</span>
                  <span className={styles.categoryCount}>{stats.enabled}/{stats.total}</span>
                </button>
                
                {categories.map(cat => (
                  <button
                    key={cat.key}
                    className={`${styles.categoryItem} ${selectedCategory === cat.key ? styles.categoryActive : ''}`}
                    onClick={() => setSelectedCategory(cat.key)}
                  >
                    <span className={styles.categoryIcon}>{cat.icon}</span>
                    <span className={styles.categoryName}>{cat.name}</span>
                    <span className={styles.categoryCount}>{cat.enabled}/{cat.total}</span>
                    <FaChevronRight className={styles.categoryArrow} />
                  </button>
                ))}
              </div>
            </div>

            {/* Right Panel - Templates */}
            <div className={styles.templatePanel}>
              {/* Search & Category Header */}
              <div className={styles.templateHeader}>
                <div className={styles.templateHeaderTop}>
                  <h3 className={styles.templateTitle}>
                    {selectedCategory 
                      ? `${CATEGORY_METADATA[selectedCategory]?.icon || '📋'} ${CATEGORY_METADATA[selectedCategory]?.name || selectedCategory}`
                      : '📋 All Notifications'
                    }
                  </h3>
                  {selectedCategory && (
                    <button
                      className={styles.bulkToggle}
                      onClick={() => {
                        const cat = categories.find(c => c.key === selectedCategory);
                        const allEnabled = cat?.enabled === cat?.total;
                        handleCategoryBulkToggle(selectedCategory, !allEnabled);
                      }}
                    >
                      {(() => {
                        const cat = categories.find(c => c.key === selectedCategory);
                        return cat?.enabled === cat?.total ? 'Disable All' : 'Enable All';
                      })()}
                    </button>
                  )}
                </div>
                
                <div className={styles.searchBox}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search notifications..."
                    className={styles.searchInput}
                  />
                </div>
              </div>

              {/* Template List */}
              <div className={styles.templateList}>
                {displayedTemplates.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>No notifications found</p>
                  </div>
                ) : (
                  displayedTemplates.map(template => {
                    const isEnabled = getNotificationEnabled(template);
                    const isCritical = CRITICAL_NOTIFICATION_TYPES.includes(template.type);
                    
                    return (
                      <div 
                        key={template._id || template.type}
                        className={`${styles.templateRow} ${!isEnabled ? styles.templateDisabled : ''}`}
                      >
                        <div className={styles.templateInfo}>
                          <div className={styles.templateNameRow}>
                            <span className={styles.templateName}>{template.name}</span>
                            {isCritical && (
                              <span className={styles.criticalBadge}>
                                <FaExclamationTriangle /> Critical
                              </span>
                            )}
                            {template.isCustomized && (
                              <span className={styles.customizedBadge}>Customized</span>
                            )}
                          </div>
                          {!selectedCategory && (
                            <span className={styles.templateCategory}>
                              {CATEGORY_METADATA[template.category]?.icon} {CATEGORY_METADATA[template.category]?.name || template.category}
                            </span>
                          )}
                        </div>
                        
                        <div className={styles.templateActions}>
                          <button
                            className={styles.editBtn}
                            onClick={() => handleEditTemplate(template)}
                            title="Customize template"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleNotificationToggle(template.type, !getNotificationSettings(template)?.enabled)}
                            className={styles.toggleBtn}
                            disabled={!notificationSettings?.globalSettings?.allNotificationsEnabled}
                          >
                            {getNotificationSettings(template)?.enabled ? (
                              <FaToggleOn className={styles.toggleOn} />
                            ) : (
                              <FaToggleOff className={styles.toggleOff} />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Accordion (when not in custom mode but showing overview) */}
      {presetMode !== 'custom' && (
        <div className={styles.overviewSection}>
          <h2 className={styles.sectionTitle}>Notification Overview</h2>
          <div className={styles.categoryAccordion}>
            {categories.map(cat => (
              <div key={cat.key} className={styles.accordionItem}>
                <button 
                  className={styles.accordionHeader}
                  onClick={() => toggleCategoryExpand(cat.key)}
                >
                  <span className={styles.accordionIcon}>{cat.icon}</span>
                  <span className={styles.accordionName}>{cat.name}</span>
                  <span className={styles.accordionProgress}>
                    <span 
                      className={styles.progressBar} 
                      style={{ width: `${(cat.enabled / cat.total) * 100}%` }}
                    />
                  </span>
                  <span className={styles.accordionCount}>{cat.enabled}/{cat.total}</span>
                  {expandedCategories[cat.key] ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                
                {expandedCategories[cat.key] && (
                  <div className={styles.accordionContent}>
                    {(templatesByCategory[cat.key] || []).map(template => (
                      <div 
                        key={template._id || template.type}
                        className={styles.accordionTemplate}
                      >
                        <span className={styles.accordionTemplateName}>
                          {template.name}
                          {CRITICAL_NOTIFICATION_TYPES.includes(template.type) && (
                            <FaExclamationTriangle className={styles.criticalIcon} />
                          )}
                        </span>
                        {getNotificationEnabled(template) ? (
                          <FaCheckCircle className={styles.enabledIcon} />
                        ) : (
                          <FaTimesCircle className={styles.disabledIcon} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
